import type { FC } from 'react';

import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { Country, TvDashboard, TvDevice } from 'src/api/zrm';
import { DashboardStatus } from 'src/tv/dashboardParts';
import KhDashboard from 'src/tv/KhDashboard';
import MbDashboard from 'src/tv/MbDashboard';
import { COUNTRY_LABELS, dashboardLabel } from 'src/tv/pairingCode';
import TvFrame from 'src/tv/TvFrame';
import { TV_COLORS } from 'src/tv/tvTheme';
import { useTvDashboard } from 'src/tv/useTvDashboard';
import { useTvDevice } from 'src/tv/useTvDevice';

interface TvPairedScreenProps {
  dashboard: TvDashboard;
  token: string;
  /** Null right after a reload, until the first `/tv/me` answers. */
  device: TvDevice | null;
  onDevice: (device: TvDevice) => void;
  /** Called once per query that gets a 401 (both, for a revoked token after a reload), so it must be idempotent. */
  onUnpaired: () => void;
}

/**
 * The paired screen. Polls `/tv/dashboard` every minute and draws the MB or KH dashboard, keeping the
 * last numbers on screen through an outage. `/tv/me` is re-checked on the same beat for the screen's
 * name and country; a 401 from either falls back to pairing.
 */
const TvPairedScreen: FC<TvPairedScreenProps> = (props) => {
  const { dashboard, token, device, onDevice, onUnpaired } = props;
  const { t } = useTranslation();

  const { snapshot, hasAnswered, offlineSince: dashboardOfflineSince } = useTvDashboard(dashboard, token, onUnpaired);
  const deviceOfflineSince = useTvDevice(dashboard, token, device, onDevice, onUnpaired);
  const country = snapshot?.country ?? device?.country ?? Country.SE;
  const outageSince = [deviceOfflineSince, dashboardOfflineSince].filter((since): since is number => since !== null);
  const status = (
    <DashboardStatus
      country={country}
      timezone={snapshot?.timezone}
      generatedAt={snapshot?.generated_at ?? null}
      offlineSince={outageSince.length ? Math.min(...outageSince) : null}
    />
  );

  if (snapshot?.mb && dashboard === TvDashboard.Mb)
    return (
      <TvFrame
        isDense
        aside={status}
      >
        <MbDashboard
          snapshot={snapshot}
          mb={snapshot.mb}
        />
      </TvFrame>
    );

  if (snapshot?.kh && dashboard === TvDashboard.Kh)
    return (
      <TvFrame
        isDense
        aside={status}
      >
        <KhDashboard
          snapshot={snapshot}
          kh={snapshot.kh}
        />
      </TvFrame>
    );

  // Connected, but zrm has not built this screen's first snapshot yet (it starts once a screen is
  // paired and runs every minute), or the first answer is still on its way.
  return (
    <TvFrame aside={status}>
      <Box sx={{ mt: '5vh' }}>
        <Typography
          component="h1"
          data-testid="tv-device-name"
          sx={{ fontSize: '3.4vw', fontWeight: 600, lineHeight: 1.1, letterSpacing: '-0.02em' }}
        >
          {device?.name ?? t('Connecting to ZRM…')}
        </Typography>
        <Typography sx={{ mt: '1.2vh', fontSize: '1.7vw', color: TV_COLORS.muted }}>
          {dashboardLabel(t, dashboard)}
          {device && ` · ${t(COUNTRY_LABELS[device.country])}`}
        </Typography>
      </Box>

      <Box
        sx={{
          flex: 1,
          mt: '5vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderTop: `1px solid ${TV_COLORS.line}`,
        }}
      >
        <Typography sx={{ fontSize: '1.9vw', color: TV_COLORS.muted, textAlign: 'center', maxWidth: '40ch', lineHeight: 1.4 }}>
          {hasAnswered
            ? t('This screen is connected. The first numbers arrive within a minute.')
            : t('Loading the numbers…')}
        </Typography>
      </Box>
    </TvFrame>
  );
};

export default TvPairedScreen;
